/**
 * EvalUserPicker - User selector for evaluation runs
 * Allows admin to pick which user context the evaluation agent uses
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, User, Star, AlertTriangle, Shield, TestTube, UserCheck, X } from 'lucide-react';
import { usersApi } from '@/lib/api/users';
import { useI18n } from '@/i18n';

interface EvalUserPickerProps {
  selectedUserId: string | null;
  onSelect: (userId: string | null) => void;
}

export const EvalUserPicker: React.FC<EvalUserPickerProps> = ({ selectedUserId, onSelect }) => {
  const { messages } = useI18n();
  const t = messages.evalScenarios.userPicker;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['eval-users', debouncedSearch],
    queryFn: () => usersApi.listUsersComplete({
      page: 1,
      page_size: 50,
      sort_by: 'created_at',
      sort_order: 'desc',
      search: debouncedSearch || undefined,
    }),
  });

  const users = data?.users || [];
  const isTestUser = (userId: string) => userId.startsWith('test_') || userId.startsWith('test-');
  const truncateId = (id: string) => id.length > 20 ? id.slice(0, 8) + '...' + id.slice(-8) : id;

  // Find the selected user's display name from the list, or show truncated ID
  const selectedUserInfo = useMemo(() => {
    if (!selectedUserId) return null;
    const found = users.find((u) => u.user_id === selectedUserId);
    return {
      name: found?.display_name || truncateId(selectedUserId),
      hasChart: found?.has_astro_profile ?? false,
      isCurrentUser: selectedUserId === localStorage.getItem('admin_user_id'),
    };
  }, [selectedUserId, users]);

  // Sort: test users last, has_astro_profile first, admins first
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const aIsTest = a.user_id.startsWith('test_') || a.user_id.startsWith('test-');
      const bIsTest = b.user_id.startsWith('test_') || b.user_id.startsWith('test-');
      if (aIsTest !== bIsTest) return aIsTest ? 1 : -1;
      if (a.has_astro_profile !== b.has_astro_profile) return a.has_astro_profile ? -1 : 1;
      if (a.is_admin !== b.is_admin) return a.is_admin ? -1 : 1;
      return 0;
    });
  }, [users]);

  const handleUseCurrentUser = () => {
    const currentUserId = localStorage.getItem('admin_user_id');
    if (currentUserId) {
      onSelect(currentUserId);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-indigo-500" />
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            {t.title}
          </span>
        </div>
        <button
          onClick={handleUseCurrentUser}
          className={`inline-flex items-center gap-1 text-xs font-medium transition-colors ${
            selectedUserInfo?.isCurrentUser
              ? 'text-emerald-600'
              : 'text-indigo-500 hover:text-indigo-700'
          }`}
        >
          <UserCheck className="w-3.5 h-3.5" />
          {selectedUserInfo?.isCurrentUser ? t.currentUserActive : t.useCurrentUser}
        </button>
      </div>

      {/* Selected user banner */}
      {selectedUserId && selectedUserInfo && (
        <div className="flex items-center justify-between p-2.5 bg-indigo-50 border border-indigo-200 rounded-lg">
          <div className="flex items-center gap-2 min-w-0">
            <UserCheck className="w-4 h-4 text-indigo-600 flex-shrink-0" />
            <div className="min-w-0">
              <span className="text-sm font-medium text-indigo-700 truncate block">
                {selectedUserInfo.name}
              </span>
              {selectedUserInfo.isCurrentUser && (
                <span className="text-[10px] text-indigo-500">{t.currentUserLabel}</span>
              )}
            </div>
            {selectedUserInfo.hasChart && (
              <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">
                <Star className="w-3 h-3" />
                {t.hasChart}
              </span>
            )}
          </div>
          <button
            onClick={() => onSelect(null)}
            className="flex-shrink-0 p-1 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100 rounded transition-colors"
            title={t.clearSelection}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* No selection notice */}
      {!selectedUserId && (
        <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-700">
            <p className="font-medium">{t.noUserSelected}</p>
            <p>{t.noUserSelectedHint}</p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder={t.searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      {/* User list */}
      <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
        {isLoading ? (
          <div className="text-center text-sm text-gray-400 py-4">{t.loading}</div>
        ) : sortedUsers.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-4">{t.noMatch}</div>
        ) : (
          sortedUsers.map((user) => {
            const isSelected = selectedUserId === user.user_id;
            const testUser = isTestUser(user.user_id);

            return (
              <button
                key={user.user_id}
                onClick={() => onSelect(isSelected ? null : user.user_id)}
                className={`w-full text-left p-2.5 rounded-lg border transition-all ${
                  isSelected
                    ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-400'
                    : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`text-sm font-medium truncate ${isSelected ? 'text-indigo-700' : 'text-gray-900'}`}>
                      {user.display_name}
                    </span>
                    {user.is_admin && (
                      <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">
                        <Shield className="w-3 h-3" />
                        Admin
                      </span>
                    )}
                    {testUser && (
                      <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700">
                        <TestTube className="w-3 h-3" />
                        {t.testBadge}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {user.has_astro_profile ? (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700" title={t.hasChartTooltip}>
                        <Star className="w-3 h-3" />
                        {t.hasChart}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-600" title={t.noChartTooltip}>
                        <AlertTriangle className="w-3 h-3" />
                        {t.noChart}
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-400">
                  <span className="truncate max-w-[180px]" title={user.user_id}>
                    {truncateId(user.user_id)}
                  </span>
                  {user.real_name && user.real_name !== user.display_name && (
                    <span>· {user.real_name}</span>
                  )}
                  {user.role_level !== 'free' && (
                    <span className="text-blue-500">· {user.role_level}</span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Total count */}
      {data && data.total > 0 && (
        <div className="text-[11px] text-gray-400 text-right">
          {t.showingCount.replace('{shown}', String(Math.min(50, data.total))).replace('{total}', String(data.total))}
        </div>
      )}
    </div>
  );
};
